import * as authSelectors from '@redux/reducers/auth';
import { store } from '@redux/storeConfig/store';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import { select, takeEvery } from 'redux-saga/effects';
import * as entitySelectors from '../../Entities/reducers';

import * as actions from '../actions';
import * as schemas from '../schemas';
import {
  getSystemNotificationsListService,
  getUserNotificationsListService,
  markUserNotificationsAsReadService,
  markSystemNotificationsAsReadService,
  markUserNotificationsAsForwardService
} from '../services';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                         FETCH USER NOTIFICATIONS LIST                      */
/* -------------------------------------------------------------------------- */
function* fetchUserNotificationsList(action) {
  const {
    operatorId,
    organizationId,
    applicationIdentity,
    routeStatusId,
    search,
    startDate,
    endDate
  } = action.payload;

  if (operatorId && organizationId) {
    getUserNotificationsListService(
      {
        operatorId,
        organizationId,
        applicationIdentity,
        routeStatusId,
        search,
        startDate,
        endDate
      },
      {
        successAction: (response) => {
          const resultData = response.data;
          const resultNotices = resultData.noticeList.map((result) => camelcaseKeys(result));
          const resultEvents = resultData.eventList.map((result) => camelcaseKeys(result));

          const {
            entities: { userNotifications: notices },
            result
          } = normalize(resultNotices, schemas.userNotifications);
          store.dispatch(actions.completeFetchingUserNotifications(notices, result));

          const {
            entities: { userNotifications: events },
            result: eventResult
          } = normalize(resultEvents, schemas.userNotifications);
          store.dispatch(actions.completeFetchingUserEvents(events, eventResult));
        },
        errorAction: (error) => {
          store.dispatch(actions.failFetchingUserNotifications(error));
        }
      }
    );
  }
}

export function* watchFetchUserNotificationsList() {
  yield takeEvery(types.USER_NOTIFICATIONS_FETCH_STARTED, fetchUserNotificationsList);
}

/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchUserNotificationsList(action) {
  const {
    entityTypeId,
    operatorId: filterOperatorId,
    organizationId: filterOrganizationId
  } = action.payload;
  const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  getSystemNotificationsListService(
    { operatorId, organizationId },
    {
      successAction: (response) => {
        const resultData = response.data;
        const resultNotices = resultData.noticeList.map((result) => camelcaseKeys(result));
        const resultEvents = resultData.eventList.map((result) => camelcaseKeys(result));
        const {
          entities: { notices },
          result
        } = normalize(resultNotices, schemas.userNotifications);

        const {
          entities: { events },
          result: eventResult
        } = normalize(resultEvents, schemas.userNotifications);
        store.dispatch(
          actions.completePartialFetchingUserNotifications(
            notices,
            result,
            operatorId,
            organizationId
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingUserNotifications(error));
      }
    }
  );
}

export function* watchPartialFetchUserNotificationsList() {
  yield takeEvery(
    types.USER_NOTIFICATIONS_PARTIAL_FETCH_STARTED,
    partialFetchUserNotificationsList
  );
}

function* markAsReadUserNotifications(action) {
  const notificationIds = action.payload;

  markUserNotificationsAsReadService(
    { notificationIds: notificationIds.join(',') },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const resultIds = resultData.map((result) => result.notificationId);

        store.dispatch(
          actions.completeReadingUserNotification(
            resultData,
            resultData.map((result) => result.notificationId)
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failReadingUserNotification(notificationIds));
      }
    }
  );
}

export function* watchMarkAsReadUserNotification() {
  yield takeEvery(types.USER_NOTIFICATION_READ_STARTED, markAsReadUserNotifications);
}

function* markAsReadSystemNotifications(action) {
  const notificationIds = action.payload;

  markSystemNotificationsAsReadService(
    { notificationIds: notificationIds.join(',') },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));

        store.dispatch(
          actions.completeReadingSystemNotification(
            resultData,
            resultData.map((result) => result.notificationId)
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failReadingSystemNotification(notificationIds));
      }
    }
  );
}

export function* watchMarkAsReadSystemNotification() {
  yield takeEvery(types.SYSTEM_NOTIFICATION_READ_STARTED, markAsReadSystemNotifications);
}

function* forwardUserNotifications(action) {
  const notificationIds = action.payload;

  markUserNotificationsAsForwardService(
    { notificationIds: notificationIds.join(',') },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));

        store.dispatch(
          actions.completeForwardingUserNotification(
            resultData,
            resultData.map((result) => result.notificationId)
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failForwardingUserNotification(notificationIds));
      }
    }
  );
}

export function* watchForwardUserNotification() {
  yield takeEvery(types.USER_NOTIFICATION_FORWARD_STARTED, forwardUserNotifications);
}
